import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { formatCurrency } from '../../utils/formatters';
import Pagination from '../Pagination';

const SalesView = ({ 
    sales, 
    openModal, 
    metrics, 
    inventory, 
    brands, 
    selectedIds, 
    onSelectAll, 
    onSelectOne, 
    onDeleteSelected,
    onCustomerSelect,
    scrollTo,
    onScrollComplete
}) => {
    const tableRef = useRef(null);
    const [expandedSaleId, setExpandedSaleId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [filters, setFilters] = useState({
        search: '',
        brand: 'all',
        category: 'all',
        status: 'all'
    });
    
    const [selectedYear, setSelectedYear] = useState('all');
    const [selectedMetric, setSelectedMetric] = useState('monthlyRevenue');
    
    const [sort, setSort] = useState({ key: 'saleDate', direction: 'desc' });

    // EFEITO ADICIONADO: Rola a tela até a tabela quando solicitado pelo Dashboard
    useEffect(() => {
        if (scrollTo === 'historico-vendas') {
            tableRef.current?.scrollIntoView({ behavior: 'smooth' });
            onScrollComplete(); // Informa ao App.js que a rolagem foi concluída
        }
    }, [scrollTo, onScrollComplete]);
				
    const categories = useMemo(() => {
        if (!inventory) return [];
        return [...new Set(inventory.map(p => p.category))];
    }, [inventory]);

    const displayMetrics = useMemo(() => {
        if (selectedYear === 'all' || !metrics.byYear[selectedYear]) {
            return metrics.global;
        }
        return metrics.byYear[selectedYear];
    }, [selectedYear, metrics]);
    
    const monthlyChartData = useMemo(() => {
        const dataForYear = metrics.byYear[selectedYear] || { monthlyRevenue: Array(12).fill(0), monthlyProfit: Array(12).fill(0) };
        const data = selectedMetric === 'monthlyRevenue' ? dataForYear.monthlyRevenue : dataForYear.monthlyProfit;
        const label = selectedMetric === 'monthlyRevenue' ? 'Faturamento Bruto' : 'Lucro Bruto';

        return {
            labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
            datasets: [{
                label: `${label} em ${selectedYear}`,
                data: data,
                backgroundColor: 'rgba(215, 191, 189, 0.6)',
                borderColor: 'rgba(215, 191, 189, 1)',
                borderWidth: 1,
            }]
        };
    }, [selectedYear, selectedMetric, metrics.byYear]);
    
    const topProductsData = useMemo(() => {
        return {
            labels: metrics.topProducts.map(p => p[0]),
            datasets: [{
                label: 'Faturamento por Produto',
                data: metrics.topProducts.map(p => p[1]),
                backgroundColor: 'rgba(215, 191, 189, 0.6)',
            }]
        }
    }, [metrics.topProducts]);
    
    const topBrandsData = useMemo(() => {
        return {
            labels: metrics.topBrands.map(b => b[0]),
            datasets: [{
                label: 'Faturamento por Marca',
                data: metrics.topBrands.map(b => b[1]),
                backgroundColor: 'rgba(215, 191, 189, 0.6)',
            }]
        }
    }, [metrics.topBrands]);

    const filteredAndSortedSales = useMemo(() => {
        if (!sales) return [];
        
        let items = sales.map(sale => {
            const firstItem = sale.items && sale.items.length > 0 ? sale.items[0] : null;
            const product = inventory.find(p => p.id === (firstItem?.productId || sale.productId));
            return { 
                ...sale, 
                category: product ? product.category : 'N/A',
                representativeName: firstItem ? firstItem.productName : (sale.productName || 'Venda sem Itens'),
                totalQuantity: sale.totalQuantity ?? sale.quantity
            };
        });

        items = items.filter(sale => {
            const searchLower = filters.search.toLowerCase();
            const matchesSearch = (sale.representativeName?.toLowerCase() || '').includes(searchLower) || 
                                  (sale.customerName?.toLowerCase() || '').includes(searchLower);
            const saleBrand = inventory.find(p => p.id === (sale.items[0]?.productId || sale.productId))?.brand;
            const matchesBrand = filters.brand === 'all' || saleBrand === filters.brand;
            const matchesCategory = filters.category === 'all' || sale.category === filters.category;
            const matchesStatus = filters.status === 'all' || sale.status === filters.status;
            return matchesSearch && matchesBrand && matchesCategory && matchesStatus;
        });

        items.sort((a, b) => {
            const valA = a[sort.key] || '';
            const valB = b[sort.key] || '';
            
            if (valA instanceof Date && valB instanceof Date) {
                return sort.direction === 'asc' ? valA.getTime() - valB.getTime() : valB.getTime() - valA.getTime();
            }
            if (typeof valA === 'string' && typeof valB === 'string') {
                return sort.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            if (typeof valA === 'number' && typeof valB === 'number') {
                 return sort.direction === 'asc' ? valA - valB : valB - valA;
            }
            return 0;
        });

        return items;
    }, [sales, inventory, filters, sort]);

    const paginatedSales = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredAndSortedSales.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredAndSortedSales, currentPage, itemsPerPage]);

    const isAllSelectedOnPage = useMemo(() => {
        if (paginatedSales.length === 0) return false;
        return selectedIds && paginatedSales.every(s => selectedIds.includes(s.id));
    }, [paginatedSales, selectedIds]);

    const handleItemsPerPageChange = useCallback((value) => {
        setItemsPerPage(Number(value));
        setCurrentPage(1);
    }, []);

    const handleSort = useCallback((key) => {
        setSort(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
    }, []);
    
    const handleToggleExpand = (saleId) => {
        setExpandedSaleId(prevId => (prevId === saleId ? null : saleId));
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'Concluída': return 'bg-green-100 text-green-800';
            case 'Pago Parcialmente': return 'bg-yellow-100 text-yellow-800';
            case 'Pendente': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };
    
    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800 mb-4 sm:mb-0">Relatório de Vendas</h2>
                <div className="flex items-center">
                    <label htmlFor="year-select" className="mr-2 text-sm font-medium">Ano:</label>
                    <select id="year-select" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D7BFBD]">
                        <option value="all">Todos</option>
                        {metrics.availableYears.map(year => <option key={year} value={year}>{year}</option>)}
                    </select>
                </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                    <h2 className="text-sm font-medium text-[#9B9A98]">Faturamento Bruto</h2>
                    <p className="text-3xl font-bold text-gray-800 mt-2">{formatCurrency(displayMetrics.totalRevenue)}</p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                    <h2 className="text-sm font-medium text-[#9B9A98]">Lucro Bruto</h2>
                    <p className="text-3xl font-bold text-gray-800 mt-2">{formatCurrency(displayMetrics.totalProfit)}</p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                    <h2 className="text-sm font-medium text-[#9B9A98]">Ticket Médio</h2>
                    <p className="text-3xl font-bold text-gray-800 mt-2">{formatCurrency(displayMetrics.avgTicket)}</p>
                </div>
            </div>
            
            {selectedYear !== 'all' && (
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                     <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-bold text-gray-800">Análise Mensal</h2>
                        <div className="flex items-center gap-2">
                             <button onClick={() => setSelectedMetric('monthlyRevenue')} className={`px-3 py-1 text-sm rounded-lg ${selectedMetric === 'monthlyRevenue' ? 'custom-button' : 'bg-gray-200'}`}>Faturamento</button>
                             <button onClick={() => setSelectedMetric('monthlyProfit')} className={`px-3 py-1 text-sm rounded-lg ${selectedMetric === 'monthlyProfit' ? 'custom-button' : 'bg-gray-200'}`}>Lucro</button>
                        </div>
                    </div>
                     <div className="h-72">
                        <Bar data={monthlyChartData} options={{ responsive: true, maintainAspectRatio: false, scales: { y: { ticks: { callback: value => formatCurrency(value) } } } }}/>
                     </div>
                </div>
            )}
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                     <h2 className="text-lg font-bold text-gray-800 mb-4">Top 5 Produtos por Faturamento</h2>
                     <div className="h-64"><Bar data={topProductsData} options={{ responsive: true, maintainAspectRatio: false, indexAxis: 'y', scales: { x: { ticks: { callback: value => formatCurrency(value) } } } }}/></div>
                </div>
                 <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                     <h2 className="text-lg font-bold text-gray-800 mb-4">Top 5 Marcas por Faturamento</h2>
                     <div className="h-64"><Bar data={topBrandsData} options={{ responsive: true, maintainAspectRatio: false, indexAxis: 'y', scales: { x: { ticks: { callback: value => formatCurrency(value) } } } }}/></div>
                </div>
            </div>

            <div ref={tableRef} id="historico-vendas" className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                <h2 className="text-lg font-bold text-gray-800 mb-4">Histórico de Vendas</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <input type="text" value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} placeholder="🔎 Pesquisar..." className="w-full px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg"/>
                    <select value={filters.brand} onChange={e => setFilters({...filters, brand: e.target.value})} className="w-full px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg">
                        <option value="all">Todas as marcas</option>
                        {brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                    </select>
                    <select value={filters.category} onChange={e => setFilters({...filters, category: e.target.value})} className="w-full px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg">
                        <option value="all">Todas as categorias</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
					<select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} className="w-full px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg">
                        <option value="all">Todos os Status</option>
                        <option value="Pendente">Pendente</option>
                        <option value="Pago Parcialmente">Pago Parcialmente</option>
                        <option value="Concluída">Concluída</option>
                    </select>
                </div>
                
                {selectedIds.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex justify-between items-center mb-4">
                        <span className="text-sm font-medium text-blue-800">{selectedIds.length} {selectedIds.length > 1 ? 'vendas selecionadas' : 'venda selecionada'}</span>
                        <button onClick={onDeleteSelected} className="flex items-center gap-2 text-sm font-semibold text-red-600 hover:text-red-800">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            Excluir Selecionados
                        </button>
                    </div>
                )}
                
                {/* START: Mobile View */}
                <div className="space-y-4 md:hidden">
                    <div className="flex items-center justify-between p-2">
                        <label className="flex items-center text-sm font-medium">
                            <input 
                                type="checkbox"
                                className="h-4 w-4 mr-2 rounded border-gray-300"
                                checked={isAllSelectedOnPage}
                                onChange={() => onSelectAll(paginatedSales.map(s => s.id), isAllSelectedOnPage)}
                            />
                            Selecionar Todos
                        </label>
                    </div>
                    {paginatedSales.map(s => {
                        const isExpanded = expandedSaleId === s.id;
                        return (
                            <div key={s.id} className="border rounded-lg p-4 space-y-3">
                                <div className="flex justify-between items-start">
                                    <div className="flex items-start gap-3">
                                        <input type="checkbox" className="h-4 w-4 mt-1 rounded" checked={selectedIds.includes(s.id)} onChange={() => onSelectOne(s.id)} />
                                        <div>
                                            <h3 className="font-bold text-gray-800">{s.representativeName}{s.items && s.items.length > 1 ? ` (+${s.items.length - 1})` : ''}</h3>
                                            <p className="text-sm text-gray-600 hover:text-[#D7BFBD] cursor-pointer" onClick={() => s.customerId && onCustomerSelect(s.customerId)}>
                                                {s.customerName || 'Cliente não informado'}
                                            </p>
                                        </div>
                                    </div>
                                    <p className="font-bold text-lg text-gray-800 flex-shrink-0 ml-2">{formatCurrency(s.totalAmount)}</p>
                                </div>
                                <div className="flex justify-between items-center pt-3 border-t">
                                    <div className="text-sm">
                                        <p className="text-gray-500">{s.saleDate ? s.saleDate.toLocaleDateString('pt-BR') : 'N/A'}</p>
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusClass(s.status)}`}>{s.status || 'N/A'}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {s.items && s.items.length > 1 && <button className="action-btn text-xl" onClick={() => handleToggleExpand(s.id)}>{isExpanded ? '▲' : '▼'}</button>}
                                        {s.status !== 'Concluída' && (<button className="action-btn text-xl" onClick={() => openModal('addPayment', s)} title="Adicionar Pagamento">💰</button>)}
                                        <button className="action-btn delete-btn text-xl" onClick={() => openModal('confirmDelete', { id: s.id, type: 'sale', name: `venda de ${s.representativeName}` })}>🗑️</button>
                                    </div>
                                </div>
                                {isExpanded && (
                                    <div className="pt-2 mt-2 border-t text-sm space-y-1">
                                        <h4 className="font-semibold text-gray-600">Itens:</h4>
                                        {s.items.map((item, index) => (
                                            <p key={index} className="flex justify-between text-gray-700">
                                                <span>{item.quantity}x {item.productName}</span>
                                                <span>{formatCurrency(item.unitPrice * item.quantity)}</span>
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
                {/* END: Mobile View */}

                {/* START: Desktop View */}
                <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-left">
                        <thead className="border-b-2 border-[#F2DFDB]">
                            <tr>
                                <th className="p-4 w-4"><input type="checkbox" className="h-4 w-4 rounded border-gray-300" checked={isAllSelectedOnPage} onChange={() => onSelectAll(paginatedSales.map(s => s.id), isAllSelectedOnPage)} /></th>
                                <th className="p-4 w-8"></th>
                                <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleSort('saleDate')}>Data {sort.key === 'saleDate' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleSort('representativeName')}>Produto(s) {sort.key === 'representativeName' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleSort('customerName')}>Cliente {sort.key === 'customerName' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold text-gray-700 sortable-header text-center" onClick={() => handleSort('totalQuantity')}>Qtd. Total {sort.key === 'totalQuantity' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleSort('totalAmount')}>Valor Total {sort.key === 'totalAmount' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold text-gray-700 sortable-header" onClick={() => handleSort('status')}>Status {sort.key === 'status' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold text-gray-700 text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedSales.map(s => {
                                const isExpanded = expandedSaleId === s.id;
                                return (
                                    <React.Fragment key={s.id}>
                                        <tr className="border-b border-[#F2DFDB] hover:bg-[#F4EFEC]">
                                            <td className="p-4"><input type="checkbox" className="h-4 w-4 rounded border-gray-300" checked={selectedIds.includes(s.id)} onChange={() => onSelectOne(s.id)} /></td>
                                            <td className="p-4 text-center">{s.items && s.items.length > 1 && (<button onClick={() => handleToggleExpand(s.id)} className="text-lg">{isExpanded ? '▲' : '▼'}</button>)}</td>
                                            <td className="p-4 text-gray-600">{s.saleDate ? s.saleDate.toLocaleDateString('pt-BR') : 'N/A'}</td>
                                            <td className="p-4 font-medium text-gray-800">{s.representativeName}</td>
                                            <td className="p-4 font-medium text-gray-800">{s.customerId ? (<button onClick={() => onCustomerSelect(s.customerId)} className="hover:text-[#D7BFBD]">{s.customerName}</button>) : (s.customerName || 'N/A')}</td>
                                            <td className="p-4 text-gray-800 font-medium text-center">{s.totalQuantity}</td>
                                            <td className="p-4 text-gray-800 font-medium">{formatCurrency(s.totalAmount)}</td>
                                            <td className="p-4"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusClass(s.status)}`}>{s.status || 'N/A'}</span></td>
                                            <td className="p-4"><div className="flex items-center justify-center gap-2">{s.status !== 'Concluída' && (<button className="action-btn" onClick={() => openModal('addPayment', s)} title="Adicionar Pagamento">💰</button>)}<button className="action-btn delete-btn" onClick={() => openModal('confirmDelete', { id: s.id, type: 'sale', name: `venda de ${s.representativeName}` })}>🗑️</button></div></td>
                                        </tr>
                                        {isExpanded && (
                                            <tr className="bg-gray-50"><td colSpan="9" className="p-4"><div className="space-y-2"><h4 className="font-semibold">Itens da Venda:</h4>{s.items.map((item, index) => (<div key={index} className="flex justify-between text-sm ml-4"><span>{item.quantity}x {item.productName}</span><span>{formatCurrency(item.unitPrice * item.quantity)}</span></div>))}</div></td></tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {/* END: Desktop View */}

                 <Pagination 
                    totalItems={filteredAndSortedSales.length}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={handleItemsPerPageChange}
                 />
            </div>
        </div>
    );
};

export default SalesView;
